import { useEffect, useState } from 'react';

/**
 * Which of the given element ids is currently the one being read.
 *
 * Used by the section navigation to mark a pill `aria-current`. Returns the
 * first id until something better is known, so the nav never renders with
 * nothing selected.
 *
 * Two things worth knowing about the implementation:
 *
 * **It guards `IntersectionObserver`.** jsdom does not implement it, and this
 * hook runs inside the site header, which nearly every component test renders.
 * Without the guard the entire suite fails with `IntersectionObserver is not
 * defined`. Same defensive shape as the `matchMedia` check in `useTheme` and the
 * `requestAnimationFrame` check in `useCountUp`.
 *
 * **It picks the topmost intersecting section, not the most visible one.**
 * Sections here are long — About alone can be taller than the viewport — so
 * "largest intersection ratio" would leave a heading highlighted while you are
 * already well into the next section. Reading order is what a reader expects a
 * nav to follow, so the rule is simply: the highest section still on screen,
 * below the header.
 */
export function useActiveSection(ids: readonly string[]): string | null {
  const [active, setActive] = useState<string | null>(ids[0] ?? null);

  useEffect(() => {
    if (typeof IntersectionObserver !== 'function') return;

    const elements = ids
      .map((id) => document.getElementById(id))
      .filter((node): node is HTMLElement => node !== null);
    if (elements.length === 0) return;

    // Keyed by id, because the callback only reports entries that *changed*.
    // Deciding from that alone would forget every section it was not told about.
    const onScreen = new Map<string, boolean>();

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          onScreen.set(entry.target.id, entry.isIntersecting);
        }
        const topmost = ids.find((id) => onScreen.get(id));
        // No section on screen means we are in the hero or the footer. Keep the
        // last answer rather than flickering to null and unsetting the pill.
        if (topmost) setActive(topmost);
      },
      {
        // Discount the two header bars at the top — 64px of brand row plus 44px
        // of section strip, matching `scroll-padding-top` in global.css — and most
        // of the lower screen, so a section counts as "current" when it reaches
        // the reading position rather than the instant it appears at the bottom
        // edge.
        rootMargin: '-112px 0px -55% 0px',
        threshold: 0,
      }
    );

    for (const element of elements) observer.observe(element);
    return () => observer.disconnect();
  }, [ids]);

  return active;
}
